import {useMemo} from 'react';
import {Dropdown, Table, Tag, Tooltip} from 'antd';
import {MoreOutlined} from '@ant-design/icons';
import PropTypes from 'prop-types';

import useIsCompact from 'utils/useIsCompact';
import {formatBalance, prettifyEnum} from 'utils/currencyFormatting';
import styles from '../wallet.module.scss';

const emptyState = (
  <div className={styles.emptyState}>
    <h3>No payment providers</h3>
    <p>Add one from the Create menu to start routing deposits.</p>
  </div>
);

const StatusTag = ({enabled}) => (
  <Tag className={styles.statusTag} color={enabled ? 'green' : 'red'}>
    {enabled ? 'Enabled' : 'Disabled'}
  </Tag>
);

StatusTag.propTypes = {enabled: PropTypes.bool};

/** Flattens a provider's `currencies` object into rows the nested table can use. */
const currencyRows = provider =>
  Object.entries(provider?.currencies ?? {}).map(([currency, value]) => ({
    ...value,
    currency,
    provider: provider?.provider,
  }));

const ProvidersTable = ({data, loading, buildCurrencyActionItems}) => {
  const isCompact = useIsCompact();

  const providers = useMemo(
    () =>
      (data ?? []).map(provider => {
        const rows = currencyRows(provider);
        return {
          ...provider,
          key: provider?.id ?? provider?.provider,
          rows,
          depositOn: rows.filter(row => row?.deposit?.enabled).length,
          withdrawalOn: rows.filter(row => row?.withdrawal?.enabled).length,
        };
      }),
    [data],
  );

  const nestedColumns = useMemo(
    () => [
      {title: 'Currency', dataIndex: 'currency', key: 'currency', width: 110},
      {
        title: 'Deposit',
        key: 'deposit',
        width: 110,
        render: (_, row) => <StatusTag enabled={!!row?.deposit?.enabled} />,
      },
      {
        title: 'Withdrawal',
        key: 'withdrawal',
        width: 120,
        render: (_, row) => <StatusTag enabled={!!row?.withdrawal?.enabled} />,
      },
      {
        title: (
          <Tooltip title="Total money sitting with this provider for this currency.">
            Provider balance
          </Tooltip>
        ),
        key: 'providerBalance',
        width: 170,
        align: 'right',
        render: (_, row) => (
          <span className={styles.money}>
            {formatBalance(row?.paymentProviderActualBalance, row?.currency)}
          </span>
        ),
      },
      {
        title: (
          <Tooltip title="Daba's own share of that balance — what the platform has earned from fees and interest.">
            Daba&apos;s share
          </Tooltip>
        ),
        key: 'platformBalance',
        width: 160,
        align: 'right',
        render: (_, row) => (
          <span className={styles.moneyMuted}>
            {formatBalance(row?.totalPlatformBalance, row?.currency)}
          </span>
        ),
      },
      {
        title: '',
        key: 'actions',
        width: 56,
        render: (_, row) => {
          const items = buildCurrencyActionItems(row);
          if (!items.length) return <span className={styles.noActions}>—</span>;
          return (
            <div
              className={styles.actionsCell}
              onClick={event => event.stopPropagation()}>
              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                menu={{items}}>
                <button
                  type="button"
                  aria-label={`${row?.provider} ${row?.currency} actions`}
                  className={styles.actionsTrigger}>
                  <MoreOutlined />
                </button>
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [buildCurrencyActionItems],
  );

  const columns = useMemo(
    () => [
      {
        title: 'Provider',
        key: 'provider',
        fixed: 'left',
        width: 180,
        render: (_, record) => (
          <span className={styles.providerName}>
            {prettifyEnum(record?.provider)}
          </span>
        ),
      },
      {
        title: 'Status',
        key: 'enabled',
        width: 110,
        render: (_, record) => <StatusTag enabled={!!record?.enabled} />,
      },
      {
        // Which currencies a provider covers used to require expanding the row.
        title: 'Currencies',
        key: 'currencies',
        width: 230,
        render: (_, record) => (
          <div className={styles.currencyChips}>
            {record.rows.length ? (
              record.rows.map(row => (
                <span className={styles.chip} key={row.currency}>
                  {row.currency}
                </span>
              ))
            ) : (
              <span className={styles.methodNone}>None</span>
            )}
          </div>
        ),
      },
      {
        title: 'Deposits on',
        key: 'depositOn',
        width: 120,
        render: (_, record) => (
          <span className={styles.methodCount}>
            <span className={styles.methodNumber}>{record.depositOn}</span>
            <span className={styles.methodWord}>of {record.rows.length}</span>
          </span>
        ),
      },
      {
        title: 'Withdrawals on',
        key: 'withdrawalOn',
        width: 140,
        render: (_, record) => (
          <span className={styles.methodCount}>
            <span className={styles.methodNumber}>{record.withdrawalOn}</span>
            <span className={styles.methodWord}>of {record.rows.length}</span>
          </span>
        ),
      },
    ],
    [],
  );

  const expandedRowRender = record => (
    <Table
      className={styles.nestedTable}
      rowKey="currency"
      columns={nestedColumns}
      dataSource={record.rows}
      pagination={false}
      size="small"
      scroll={{x: 720}}
    />
  );

  if (isCompact) {
    return (
      <div className={styles.cardList}>
        {providers.map(record => (
          <div className={styles.card} key={record.key}>
            <div className={styles.cardHeader}>
              <span className={styles.providerName}>
                {prettifyEnum(record?.provider)}
              </span>
              <StatusTag enabled={!!record?.enabled} />
            </div>

            <div className={styles.cardGrid}>
              <div className={styles.cardField}>
                <span className={styles.cardLabel}>Deposits on</span>
                <span className={styles.cardValue}>
                  {record.depositOn} of {record.rows.length}
                </span>
              </div>
              <div className={styles.cardField}>
                <span className={styles.cardLabel}>Withdrawals on</span>
                <span className={styles.cardValue}>
                  {record.withdrawalOn} of {record.rows.length}
                </span>
              </div>
            </div>

            <div className={styles.cardSection}>
              <span className={styles.expandedLabel}>Balances</span>
              {record.rows.map(row => (
                <div className={styles.cardHeader} key={row.currency}>
                  <span className={styles.chip}>{row.currency}</span>
                  <span className={styles.amountCell}>
                    <span className={styles.money}>
                      {formatBalance(
                        row?.paymentProviderActualBalance,
                        row?.currency,
                      )}
                    </span>
                    <span className={styles.moneyMuted}>
                      {formatBalance(row?.totalPlatformBalance, row?.currency)}{' '}
                      Daba&apos;s
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
        {!loading && !providers.length && emptyState}
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <Table
        rowKey="key"
        columns={columns}
        loading={loading}
        dataSource={providers}
        pagination={false}
        scroll={{x: 830}}
        expandable={{expandedRowRender, fixed: 'left', columnWidth: 44}}
        locale={{emptyText: emptyState}}
      />
    </div>
  );
};

ProvidersTable.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  buildCurrencyActionItems: PropTypes.func,
};

export default ProvidersTable;
